import { handleResponse, axiosInstance, API, graphqlAxiosInstance } from './helper';
import { getRealmUserInfo } from './keycloak';
import { Doctor, PractiseBasicDetailsRelation } from '../pages/DoctorsVerification/types';
import { getPractiseType } from '../utils';
import { ROLES, VERIFICATION_STATUS } from '../constants';

interface RoleListPayload {
  verificationStatus: string | undefined;
  roleAssigned: boolean;
  role: string;
}

interface UpdatePractiseUserRolePayload {
  role: string;
  practiseUserId: string;
}

const getRoleManagementStats = (role: string) => {
  return handleResponse(
    axiosInstance.get(`${API.getRoleManagementStats}?practiseType=${getPractiseType(role)}`)
  );
};

const getAdminRoleManagementList = (payload: RoleListPayload) =>
  handleResponse(
    axiosInstance.get(
      `${API.getAdminRoleManagementList}?verificationStatus=${payload.verificationStatus}&roleAssigned=${payload.roleAssigned}&practiseType=${getPractiseType(payload.role)}`
    )
  );

const getPractiseAdminRoleManagementList = (payload: { roleAssigned: string; role: string }) =>
  handleResponse(
    axiosInstance.get(
      `${API.getPractiseAdminRoleManagementList}?role=${payload.roleAssigned}&practiseType=${getPractiseType(payload.role)}`
    )
  );

const fetchRoleManagementTableData = async (
  limit = 10,
  updatedAt: string,
  verificationStatus = VERIFICATION_STATUS.VERIFIED,
  roleAssigned = true,
  practiseType: string,
  role = ROLES.PRACTISE_ADMIN
) => {
  const query = `
    query get_practise_user_details_by_role_assigned_and_status_V2(
      $verificationStatus: String!, 
      $roleAssigned: Boolean!, 
      $practiseType: String!, 
      $role: String!, 
      $limit: Int!, 
      $updatedAt: timestamptz
    ) @cached(ttl: 300) {
      practiseBasicDetails(
        where: {
          verificationStatus: { _eq: $verificationStatus }
          roleAssigned: { _eq: $roleAssigned }
          practiseDemographicsRelation: { practiseType: { _eq: $practiseType }}
          userPractiseMappingRelation: { role: { _eq: $role }}
          updated_at: { _lt: $updatedAt }
        },
        order_by: { updated_at: desc },
        limit: $limit
      ) {
        practiseAdminId
        practisingSince
        verificationStatus
        gender
        created_at
        updated_at
        roleAssigned
        practiseAdminLivePic {
          imageContent
        }
        practiseDemographicsRelation {
          organisationId
        }
        userPractiseMappingRelation {
          role
        }
      }

      total: practiseBasicDetails_aggregate(
        where: {
          verificationStatus: { _eq: $verificationStatus }
          roleAssigned: { _eq: $roleAssigned }
          practiseDemographicsRelation: { practiseType: { _eq: $practiseType }}
          userPractiseMappingRelation: { role: { _eq: $role }}
        }
      ) {
        aggregate {
          count(columns: id)
        }
      }
    }
  `;

  const variables = {
    verificationStatus,
    roleAssigned,
    practiseType,
    role,
    limit,
    updatedAt: updatedAt || new Date().toISOString()
  };

  const response = await graphqlAxiosInstance.post(`/${API.hasuraGraphql}`, {
    query,
    variables
  });

  return response;
};

const getRoleManagementList = async (
  limit = 10,
  created_at: string,
  verificationStatus = VERIFICATION_STATUS.VERIFIED,
  roleAssigned: boolean = true,
  practiseType: string,
  role: string = ROLES.PRACTISE_ADMIN,
  generateSignedUrl?: (url: string, token: string) => Promise<string>
) => {
  const response = await fetchRoleManagementTableData(
    limit,
    created_at,
    verificationStatus,
    roleAssigned,
    practiseType,
    role
  );

  // -----------------------------------
  // ✓ Correct backend path
  // -----------------------------------
  const basicDetailsList = response?.data?.data?.practiseBasicDetails || [];
  const totalCount = response?.data?.data?.total?.aggregate?.count || 0;

  const userAccessToken = localStorage.getItem('user_access_token') || '';

  // -----------------------------------
  // ✓ Convert API → Required Table Format
  // -----------------------------------
  const formattedRows = await Promise.all(
    basicDetailsList.map(async (item: Doctor, index: number) => {
      const {
        practiseAdminId,
        practisingSince,
        verificationStatus,
        gender,

        practiseAdminLivePic,
        practiseDemographicsRelation
      } = item;

      let email = null;
      let firstName = null;
      let lastName = null;
      if (item.practiseAdminId) {
        const keycloakData = await getRealmUserInfo(item.practiseAdminId, userAccessToken);
        email = keycloakData.email;
        firstName = keycloakData.firstName;
        lastName = keycloakData.lastName;
      }

      // Generate signed image url
      const imageUrl =
        generateSignedUrl && practiseAdminLivePic?.imageContent
          ? await generateSignedUrl(practiseAdminLivePic.imageContent, userAccessToken)
          : '';

      return {
        sNo: index + 1,
        fullName: `${firstName || ''} ${lastName || ''}`.trim() || 'N/A',
        gender: gender || 'N/A',
        practiceSince: practisingSince || 'N/A',
        doctorId: practiseAdminId || 'N/A',
        email: email || 'N/A',
        statusActions: verificationStatus || 'N/A',
        image: imageUrl,
        organisationId: practiseDemographicsRelation?.organisationId || ''
      };
    })
  );

  // -----------------------------------
  // ✓ Return final result
  // -----------------------------------
  return {
    data: formattedRows,
    hasMore: formattedRows.length === limit,
    totalCount,
    lastUpdatedAt:
      basicDetailsList.length > 0 ? basicDetailsList[basicDetailsList.length - 1].updated_at : null
  };
};

const updatePractiseUserRole = (payload: UpdatePractiseUserRolePayload) =>
  handleResponse(axiosInstance.put(API.updatePractiseUserRole, payload));

// Utility function to format doctors list
async function getFormattedRolesList(
  verificationStatus: string | undefined,
  roleAssigned: boolean,
  role: string
): Promise<Doctor[]> {
  const payload = {
    verificationStatus,
    roleAssigned,
    role
  };

  const list = await getAdminRoleManagementList(payload);

  if (!list?.practiseBasicDetails?.length) return [];

  const filteredList = list.practiseBasicDetails;

  const doctorPromises = filteredList.map(
    async (doc: {
      practiseDemographicsRelation: { organisationId: string };
      practiseAdminId: string;
    }) => {
      const user_access_token = localStorage.getItem('user_access_token') || '';

      try {
        const userInfo = await getRealmUserInfo(doc.practiseAdminId, user_access_token);

        return {
          ...doc,
          id: userInfo?.id ?? 'N/A',
          email: userInfo?.email ?? 'N/A',
          firstName: userInfo?.firstName ?? 'N/A',
          lastName: userInfo?.lastName ?? '',
          organisationId: doc.practiseDemographicsRelation.organisationId
        };
      } catch (error) {
        console.warn(`Failed to fetch data for admin ID ${doc.practiseAdminId}:`, error);
        return null;
      }
    }
  );

  const formattedDoctors = (await Promise.all(doctorPromises)).filter(Boolean);
  return formattedDoctors as Doctor[];
}

async function getFormattedPractiseAdminRolesList(
  roleAssigned: string,
  userRole: string
): Promise<Doctor[]> {
  const payload = {
    role: userRole,
    roleAssigned: roleAssigned
  };
  const list = await getPractiseAdminRoleManagementList(payload);

  if (!list?.userPractiseMapping?.length) return [];

  // Filter the list based on the selected status
  const filteredList = list?.userPractiseMapping;

  // Fetch doctor info for each filtered entry
  const doctorPromises = filteredList.map(
    async (doc: { practiseBasicDetailsRelation: PractiseBasicDetailsRelation; role: string }) => {
      try {
        const user_access_token = localStorage.getItem('user_access_token') || '';

        const { id, email, firstName, lastName } = await getRealmUserInfo(
          doc.practiseBasicDetailsRelation?.practiseAdminId,
          user_access_token
        );

        return {
          ...doc,
          id,
          role: doc?.role,
          practiseAdminLivePic: doc?.practiseBasicDetailsRelation?.practiseAdminLivePic,
          practiseAdminId: doc?.practiseBasicDetailsRelation?.practiseAdminId,
          gender: doc?.practiseBasicDetailsRelation?.gender,
          email,
          firstName,
          lastName
        };
      } catch (error) {
        console.warn(
          `Failed to fetch data for admin ID ${doc.practiseBasicDetailsRelation?.practiseAdminId}:`,
          error
        );
        return null;
      }
    }
  );

  // Resolve all promises and filter out null results
  const formattedDoctors = (await Promise.all(doctorPromises)).filter(Boolean);
  return formattedDoctors as Doctor[];
}

const insertPractiseOrganisations = (payload: { id: string; practiseAdminId: string }) =>
  handleResponse(axiosInstance.post(API.insertPractiseOrganisations, payload));

const insertPractiseDepartments = (payload: {
  id: string;
  practiseAdminId: string;
  organisationId: string;
}) => handleResponse(axiosInstance.post(API.insertPractiseDepartments, payload));

const insertUserPractiseMapping = (payload: {
  departmentId?: string;
  organisationId?: string;
  role: string;
  practiseUserId: string;
}) => handleResponse(axiosInstance.post(API.insertUserPractiseMapping, payload));

const getPractiseCode = (payload: { practiseUserId: string }) => {
  return handleResponse(axiosInstance.get(API.getPractiseCode, { params: payload }));
};

export {
  getRoleManagementStats,
  getFormattedRolesList,
  getFormattedPractiseAdminRolesList,
  insertPractiseOrganisations,
  insertPractiseDepartments,
  insertUserPractiseMapping,
  updatePractiseUserRole,
  getPractiseCode,
  getRoleManagementList
};
